<template>
  <div class="graph-kline">
    <div class="graph-header">
      <div class="graph-title">
        <symbol-logo v-if="selectedSymbolObj" :symbol="selectedSymbolObj" :size="32" />
        <div class="graph-title-text">
          <span class="graph-ticker">{{ titleTicker }}</span>
          <span v-if="titleDescription" class="graph-desc">{{ titleDescription }}</span>
        </div>
      </div>

      <div class="graph-tools">
        <b-dropdown
          size="sm"
          variant="flat-secondary"
          toggle-class="graph-tool-btn"
          menu-class="graph-tool-menu"
          right
          no-caret
        >
          <template #button-content>
            <feather-icon icon="ActivityIcon" size="14" />
            <span class="graph-tool-label">Indicators</span>
            <b-badge v-if="activeIndicatorCount" variant="info" pill>{{ activeIndicatorCount }}</b-badge>
          </template>

          <b-dropdown-header>Overlays</b-dropdown-header>
          <b-dropdown-item
            v-for="ind in mainIndicators"
            :key="ind.name"
            @click.native.capture.stop.prevent="toggleMainIndicator(ind.name)"
          >
            <span class="graph-check">{{ activeMain.includes(ind.name) ? '✓' : '' }}</span>
            <span class="graph-ind-name">{{ ind.name }}</span>
            <span class="graph-ind-label">{{ ind.label }}</span>
          </b-dropdown-item>

          <b-dropdown-divider />
          <b-dropdown-header>Sub-panes</b-dropdown-header>
          <b-dropdown-item
            v-for="ind in subIndicators"
            :key="ind.name"
            @click.native.capture.stop.prevent="toggleSubIndicator(ind.name)"
          >
            <span class="graph-check">{{ isSubActive(ind.name) ? '✓' : '' }}</span>
            <span class="graph-ind-name">{{ ind.name }}</span>
            <span class="graph-ind-label">{{ ind.label }}</span>
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          size="sm"
          variant="flat-secondary"
          toggle-class="graph-tool-btn"
          menu-class="graph-tool-menu"
          right
          no-caret
        >
          <template #button-content>
            <feather-icon icon="Edit2Icon" size="14" />
            <span class="graph-tool-label">Draw</span>
          </template>

          <b-dropdown-item
            v-for="tool in drawingTools"
            :key="tool.name"
            @click="startDrawing(tool.name)"
          >
            <span class="graph-ind-label">{{ tool.label }}</span>
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="clearDrawings">
            <span class="graph-ind-label text-danger">Clear all drawings</span>
          </b-dropdown-item>
        </b-dropdown>

        <interval-selector v-model="interval" :allowed-chart-times="symbolChartTimes" />
      </div>
    </div>

    <div ref="chartContainer" class="graph-canvas" />

    <transition name="graph-fade">
      <div v-if="loading" class="graph-overlay graph-loading">
        <div class="graph-loading-card">
          <b-spinner variant="info" />
          <span class="graph-loading-text">Loading {{ interval }}…</span>
        </div>
      </div>
    </transition>
    <div v-if="error" class="graph-overlay graph-error">{{ error }}</div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import { init, dispose } from 'klinecharts'
import IntervalSelector from '@/components/graph/intervalSelector.vue'
import SymbolLogo from '@/components/graph/symbolLogo.vue'
import { describe } from '@/components/graph/symbolMeta.js'
import { defaultInterval, FALLBACK_INTERVAL } from '@/components/graph/chartTimes.js'

import {
  fetchHistory,
  subscribeLive,
  INTERVAL_DEFAULT_LOOKBACK,
} from '@/components/graph/chart.js'


/** KLineCharts' id for the main candle pane. */
const CANDLE_PANE = 'candle_pane'

/**
 * Indicators drawn on top of the candles (IndicatorSeries.Price). AVP is the
 * seventh price-series built-in but reads `volume`, so it's excluded — see
 * toKline() below for why we don't feed volume.
 */
const MAIN_INDICATORS = [
  { name: 'MA',   label: 'Moving Average' },
  { name: 'EMA',  label: 'Exponential MA' },
  { name: 'SMA',  label: 'Smoothed MA' },
  { name: 'BOLL', label: 'Bollinger Bands' },
  { name: 'BBI',  label: 'Bull & Bear Index' },
  { name: 'SAR',  label: 'Parabolic SAR' },
]

/**
 * Oscillators that get their own pane below the candles. The remaining
 * built-ins (VOL, OBV, PVT, EMV, VR) are volume-derived and omitted.
 */
const SUB_INDICATORS = [
  { name: 'MACD', label: 'MACD' },
  { name: 'RSI',  label: 'Relative Strength' },
  { name: 'KDJ',  label: 'Stochastic (KDJ)' },
  { name: 'CCI',  label: 'Commodity Channel' },
  { name: 'DMI',  label: 'Directional Movement' },
  { name: 'WR',   label: 'Williams %R' },
  { name: 'TRIX', label: 'TRIX' },
  { name: 'ROC',  label: 'Rate of Change' },
  { name: 'MTM',  label: 'Momentum' },
  { name: 'BIAS', label: 'Bias' },
  { name: 'BRAR', label: 'BRAR' },
  { name: 'CR',   label: 'CR' },
  { name: 'DMA',  label: 'DMA' },
  { name: 'PSY',  label: 'Psychological Line' },
  { name: 'AO',   label: 'Awesome Oscillator' },
]

const DRAWING_TOOLS = [
  { name: 'horizontalStraightLine', label: 'Horizontal line' },
  { name: 'verticalStraightLine',   label: 'Vertical line' },
  { name: 'straightLine',           label: 'Trend line' },
  { name: 'rayLine',                label: 'Ray' },
  { name: 'segment',                label: 'Segment' },
  { name: 'priceLine',              label: 'Price line' },
  { name: 'priceChannelLine',       label: 'Price channel' },
  { name: 'parallelStraightLine',   label: 'Parallel lines' },
  { name: 'fibonacciLine',          label: 'Fibonacci retracement' },
  { name: 'rect',                   label: 'Rectangle' },
  { name: 'circle',                 label: 'Circle' },
  { name: 'polygon',                label: 'Polygon' },
  { name: 'simpleAnnotation',       label: 'Annotation' },
]

/** Bars visible on first paint — matches graphLight's default zoom. */
const DEFAULT_VISIBLE_BARS = 80

/**
 * Adapt a chart.js bar (Lightweight Charts shape, `time` in SECONDS) to the
 * KLineData shape (`timestamp` in MILLISECONDS).
 *
 * `volume` is deliberately not set. The backend's CandleDto.v carries
 * `tick_count`, not traded volume, and its meaning differs between the live 1m
 * aggregator and the FMP-backfilled 1h rows. Feeding it here would make the
 * volume-derived indicators silently wrong, so those are excluded from the
 * picker instead. KLineCharts treats a missing volume as 0.
 */
function toKline(bar) {
  return {
    timestamp: bar.time * 1000,
    open:  bar.open,
    high:  bar.high,
    low:   bar.low,
    close: bar.close,
  }
}

export default {
  name: 'GraphKline',
  components: { IntervalSelector, SymbolLogo },

  props: {
    value: {
      type: String,
      default: () => null
    }
  },



  data: () => ({
    selectedSymbol: 'EUR/USD',
    selectedSymbolObj: { id: 'EUR/USD', name: 'EURUSD', description: 'Euro to US Dollar', currency: 'EUR', marginCurrency: 'EUR', digits: 5 },
    selectedDigits: 5,
    interval: FALLBACK_INTERVAL,
    loading: false,
    error: null,
    chart: null,
    sub: null,
    resizeObserver: null,
    earliestMs: null,
    noMoreHistory: false,
    loadingMore: false,
    subToken: 0,
    activeMain: [],       // indicator names on the candle pane
    activeSub: [],        // [{ name, paneId }]
    mainIndicators: MAIN_INDICATORS,
    subIndicators: SUB_INDICATORS,
    drawingTools: DRAWING_TOOLS,
  }),

  computed: {
    ...mapGetters('appConfig', ['skin']),
    isDark() { return this.skin === 'dark' },
    activeIndicatorCount() { return this.activeMain.length + this.activeSub.length },
    titleTicker() {
      const s = this.selectedSymbolObj
      return (s && (s.name || s.sourceName || s.id)) || this.selectedSymbol || '—'
    },
    titleDescription() {
      return this.selectedSymbolObj ? describe(this.selectedSymbolObj) : ''
    },
    /** Optional on Symbol — undefined lets the selector use its default list. */
    symbolChartTimes() {
      return this.selectedSymbolObj ? this.selectedSymbolObj.allowedChartTimes : null
    },
  },

  watch: {
    value:{
      handler(v) { this.onSymbolSelected(v) },
      immediate: true,
    },
    interval() { this.reload() },
    isDark()   { this.applyTheme() },
  },

  created() {
    this.$root.$on('system::symbol::selected', this.onSymbolSelected)
    this.$root.$on('system::skin::changed', this.applyTheme)
  },

  beforeDestroy() {
    this.$root.$off('system::symbol::selected', this.onSymbolSelected)
    this.$root.$off('system::skin::changed', this.applyTheme)
    this.teardown()
  },

  mounted() {
    this.initChart()
    this.reload()
  },

  methods: {
    initChart() {
      const container = this.$refs.chartContainer
      this.chart = init(container, {
        locale: 'en-US',
        styles: this.chartStyles(),
      })

      this.applyPricePrecision()

      // Breathing strip on the right so the live candle isn't glued to the edge.
      this.chart.setOffsetRightDistance(60)

      // Paging: KLineCharts asks for older bars as the user pans left. We only
      // service 'forward' — the initial load is driven by reload() so the
      // loading overlay and error handling stay in one place.
      this.chart.setLoadDataCallback(this.onLoadData)

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.chart) return
        this.chart.resize()
      })
      this.resizeObserver.observe(container)
    },

    /* ---------------------------------------------------------------- theme */

    chartStyles() {
      const dark = this.isDark
      const text = dark ? '#d0d2d6' : '#4b4b4b'
      const grid = dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
      const axis = dark ? 'rgba(255,255,255,0.1)'  : 'rgba(0,0,0,0.1)'
      const up   = '#26a69a'
      const down = '#ef5350'

      return {
        grid: {
          show: true,
          horizontal: { color: grid },
          vertical:   { color: grid },
        },
        candle: {
          bar: {
            upColor: up,
            downColor: down,
            noChangeColor: text,
            upBorderColor: up,
            downBorderColor: down,
            noChangeBorderColor: text,
            upWickColor: up,
            downWickColor: down,
            noChangeWickColor: text,
          },
          priceMark: {
            high: { color: text },
            low:  { color: text },
            last: {
              upColor: up,
              downColor: down,
              noChangeColor: text,
            },
          },
          tooltip: {
            text: { color: text },
          },
        },
        indicator: {
          tooltip: { text: { color: text } },
        },
        xAxis: {
          axisLine: { color: axis },
          tickLine: { color: axis },
          tickText: { color: text },
        },
        yAxis: {
          axisLine: { color: axis },
          tickLine: { color: axis },
          tickText: { color: text },
        },
        separator: { color: axis },
        crosshair: {
          horizontal: {
            line: { color: text },
            text: { backgroundColor: dark ? '#3f4c6b' : '#686d76' },
          },
          vertical: {
            line: { color: text },
            text: { backgroundColor: dark ? '#3f4c6b' : '#686d76' },
          },
        },
      }
    },

    applyTheme() {
      if (!this.chart) return
      this.chart.setStyles(this.chartStyles())
    },

    /**
     * Axis / tooltip precision so AUD/CAD shows 0.99246 not 0.99. Volume
     * precision is 0 — nothing feeds it, but the argument is required.
     */
    applyPricePrecision() {
      if (!this.chart) return
      const d = this.selectedDigits != null ? this.selectedDigits : 5
      this.chart.setPriceVolumePrecision(d, 0)
    },

    /* ----------------------------------------------------------- data load */

    async reload() {
      if (!this.selectedSymbol || !this.chart) return
      const start = Date.now()
      this.loading = true
      this.error = null
      this.earliestMs = null
      this.noMoreHistory = false
      this.loadingMore = false

      this.closeSubscription()

      try {
        const toMs = Date.now()
        const lookbackSec = INTERVAL_DEFAULT_LOOKBACK[this.interval] || (24 * 3600)
        const fromMs = toMs - lookbackSec * 1000
        const bars = await fetchHistory(this.selectedSymbol, this.interval, fromMs, toMs)
        const kbars = bars.map(toKline)
        this.earliestMs = kbars.length ? kbars[0].timestamp : fromMs
        // `more = true` lets the load-data callback fire when the user pans left.
        this.chart.applyNewData(kbars, kbars.length > 0)
        this.applyDefaultZoom(kbars.length)
      } catch (e) {
        console.error('chart history failed', e)
        this.error = (e && e.message) || 'Failed to load chart'
      } finally {
        // Keep the overlay up for at least MIN_LOADING_MS so fast responses
        // (cache hits, small payloads) don't flicker.
        const MIN_LOADING_MS = 350
        const elapsed = Date.now() - start
        if (elapsed < MIN_LOADING_MS) {
          await new Promise(r => setTimeout(r, MIN_LOADING_MS - elapsed))
        }
        this.loading = false
      }

      const token = ++this.subToken
      this.sub = subscribeLive({
        symbol: this.selectedSymbol,
        interval: this.interval,
        onBar: (bar) => {
          // Late frames from a previous subscription must not touch the chart.
          if (token !== this.subToken) return
          if (!this.chart) return
          // updateData() replaces the bar when the timestamp matches and appends
          // otherwise, so no client-side tail bookkeeping is needed here.
          this.chart.updateData(toKline(bar))
        },
      })
    },

    /**
     * KLineCharts' paging hook. `type` is 'init' | 'forward' | 'backward';
     * 'forward' means "the user panned left, give me older bars".
     */
    async onLoadData({ type, data, callback }) {
      if (type !== 'forward') return
      if (this.loadingMore || this.noMoreHistory) { callback([], !this.noMoreHistory); return }

      const edgeMs = (data && data.timestamp) || this.earliestMs
      if (!edgeMs) { callback([], false); return }

      this.loadingMore = true
      try {
        const toMs = edgeMs - 1
        const lookbackSec = INTERVAL_DEFAULT_LOOKBACK[this.interval] || (24 * 3600)
        const fromMs = toMs - lookbackSec * 1000
        const older = await fetchHistory(this.selectedSymbol, this.interval, fromMs, toMs)
        if (!older.length) {
          this.noMoreHistory = true
          callback([], false)
          return
        }
        const kbars = older.map(toKline)
        this.earliestMs = kbars[0].timestamp
        // KLineCharts prepends these and preserves the viewport itself — no
        // save/restore of the visible range needed.
        callback(kbars, true)
      } catch (e) {
        console.error('chart paging failed', e)
        callback([], true)
      } finally {
        this.loadingMore = false
      }
    },

    /**
     * Show the last ~80 bars instead of stretching all 1000 fetched bars across
     * the screen. KLineCharts zooms by bar width, so derive it from the
     * container: 80 bars renders as ~1.3h at 1m, ~6.6h at 5m, ~3.3d at 1h.
     */
    applyDefaultZoom(barCount) {
      if (!this.chart || !barCount) return
      const container = this.$refs.chartContainer
      const width = container ? container.clientWidth : 0
      if (!width) return
      const space = width / Math.min(DEFAULT_VISIBLE_BARS, barCount)
      // Guard against absurd values on a very narrow or very wide container.
      this.chart.setBarSpace(Math.max(2, Math.min(space, 30)))
      this.chart.scrollToRealTime()
    },

    /* ------------------------------------------------------- indicators */

    isSubActive(name) {
      return this.activeSub.some(s => s.name === name)
    },

    toggleMainIndicator(name) {
      if (!this.chart) return
      const i = this.activeMain.indexOf(name)
      if (i >= 0) {
        this.chart.removeIndicator(CANDLE_PANE, name)
        this.activeMain.splice(i, 1)
      } else {
        // isStack=true so multiple overlays share the candle pane.
        this.chart.createIndicator(name, true, { id: CANDLE_PANE })
        this.activeMain.push(name)
      }
    },

    toggleSubIndicator(name) {
      if (!this.chart) return
      const i = this.activeSub.findIndex(s => s.name === name)
      if (i >= 0) {
        this.chart.removeIndicator(this.activeSub[i].paneId, name)
        this.activeSub.splice(i, 1)
      } else {
        const paneId = this.chart.createIndicator(name, false, { height: 100 })
        if (paneId) this.activeSub.push({ name, paneId })
      }
    },

    /* ---------------------------------------------------------- drawings */

    startDrawing(name) {
      if (!this.chart) return
      this.chart.createOverlay({ name })
    },

    clearDrawings() {
      if (!this.chart) return
      this.chart.removeOverlay()
    },

    /* ---------------------------------------------------------- lifecycle */

    closeSubscription() {
      if (this.sub) { try { this.sub.close() } catch (_) {} this.sub = null }
    },

    teardown() {
      this.closeSubscription()
      if (this.resizeObserver) {
        try { this.resizeObserver.disconnect() } catch (_) {}
        this.resizeObserver = null
      }
      if (this.chart) {
        try { dispose(this.$refs.chartContainer) } catch (_) {}
        this.chart = null
      }
    },

    onSymbolSelected(symbol) {
      if (!symbol) return
      // Match the value stored in candles_*.symbol — the stable Symbol.id.
      // Candles are keyed by id (not sourceName) so a feed-source switch keeps
      // the same chart series. Fall back to sourceName for older payloads.
      const name = symbol.id || symbol.sourceName
      if (!name) return
      this.selectedSymbol = name
      this.selectedSymbolObj = symbol
      if (symbol.digits != null) {
        this.selectedDigits = symbol.digits
        this.applyPricePrecision()
      }
      // Drawings are anchored to price levels of the previous instrument.
      this.clearDrawings()
      // Honour the symbol's defaultChartTime; when it (or allowedChartTimes) is
      // null the helper keeps the interval already on screen.
      const next = defaultInterval(symbol, this.interval)
      if (next !== this.interval) {
        // The `interval` watcher reloads — a second reload here would double-fetch.
        this.interval = next
        return
      }
      this.reload()
    },
  },
}
</script>

<style scoped lang="scss">
.graph-kline {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.15);
  background: transparent;
}

.graph-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  min-height: 48px;
}

.graph-title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.graph-title-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
}

.graph-ticker {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.graph-desc {
  font-size: 0.72rem;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.graph-tools {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.graph-tool-label {
  margin-left: 4px;
  font-size: 12px;
}

.graph-canvas {
  flex: 1 1 auto;
  min-height: 200px;
  background: #161d31;
}

.graph-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 14, 24, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  pointer-events: none;
  font-size: 13px;
}

.graph-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 18px 28px;
  border-radius: 12px;
  background: rgba(22, 29, 49, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
}

.graph-loading-text {
  color: #d0d2d6;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.graph-fade-enter-active,
.graph-fade-leave-active {
  transition: opacity 0.18s ease;
}
.graph-fade-enter,
.graph-fade-leave-to {
  opacity: 0;
}

.graph-error {
  color: #e02b20;
  background: rgba(224, 43, 32, 0.08);
  pointer-events: auto;
}

/* Light-mode adjustments */
body:not(.dark-layout) {
  .graph-canvas {
    background: #f9f8f4;
  }
  .graph-overlay {
    background: rgba(240, 244, 250, 0.65);
  }
  .graph-loading-card {
    background: #ffffff;
    border-color: rgba(0, 0, 0, 0.08);
  }
  .graph-loading-text {
    color: #0f172a;
  }
}
</style>

<style lang="scss">
/* Unscoped: bootstrap-vue renders dropdown menus outside the scoped subtree. */
.graph-tool-btn {
  display: flex !important;
  align-items: center;
  padding: 3px 8px !important;
}

.graph-tool-menu {
  max-height: 420px;
  overflow-y: auto;

  .dropdown-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 12px;
    padding: 4px 12px;
  }
}

.graph-check {
  display: inline-block;
  width: 12px;
  color: #00cfe8;
  font-weight: 700;
}

.graph-ind-name {
  display: inline-block;
  min-width: 42px;
  font-weight: 600;
}

.graph-ind-label {
  opacity: 0.7;
}
</style>
